/**
 * Компонент управления разделами классификаторов (Kls)
 * 
 * Функциональность:
 * - Отображение списка разделов в табличном виде
 * - Переключение между табличным и иерархическим видом
 * - Фильтрация по квалификатору
 * - Создание нового раздела
 * - Редактирование существующего раздела
 * - Удаление раздела (soft delete)
 * - Поиск по наименованию, коду и рубрике
 * - Валидация ltree формата для рубрики
 */

import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { PermissionGuard } from "@/components/PermissionGuard";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { klsSectionsApi, qualsApi } from "@/services/kls-api.service";
import type { Kls, KlsCreate, KlsUpdate, KlsHierarchyItem, KlsMoveRequest } from "@/types/api-v2";
import { klsSchema, type KlsFormData } from "@/lib/validations/kls";
import { Plus, Pencil, Trash2, Search, X, List, TreePine, Layers, FileText, GripVertical, Undo2, CheckSquare, Square, Upload, Download } from "lucide-react";
import ImportDialog from "./ImportDialog";
import ExportDialog from "./ExportDialog";
import { ReferencesSectionHeader } from "@/components/admin/ReferencesSectionHeader";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Checkbox } from "@/components/ui/checkbox";

// Компонент для перетаскиваемого элемента иерархии
interface DraggableHierarchyItemProps {
  item: KlsHierarchyItem;
  level: number;
  isLast: boolean;
  hasChildren: boolean;
  childrenCount: number;
  fullItem: Kls | null;
  onEdit: (item: Kls) => void;
  onDelete: (item: Kls) => void;
  isDragging: boolean;
  isDragOver: boolean;
  isSelected?: boolean;
  onSelect?: (id: number, selected: boolean) => void;
  isMultiSelectMode?: boolean;
  dragOverPosition?: "before" | "after" | "inside";
}

function DraggableHierarchyItem({
  item,
  level,
  isLast,
  hasChildren,
  childrenCount,
  fullItem,
  onEdit,
  onDelete,
  isDragging,
  isDragOver,
  isSelected = false,
  onSelect,
  isMultiSelectMode = false,
  dragOverPosition = "inside",
}: DraggableHierarchyItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: item.kls_id,
    disabled: !fullItem || fullItem.kls_is_del,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Определяем классы для визуальных индикаторов позиции
  const getDragOverClasses = () => {
    if (!isDragOver) return "";
    switch (dragOverPosition) {
      case "before":
        return "ring-2 ring-blue-500 bg-blue-50 border-t-2 border-t-blue-500";
      case "after":
        return "ring-2 ring-blue-500 bg-blue-50 border-b-2 border-b-blue-500";
      case "inside":
        return "ring-2 ring-blue-500 bg-blue-50";
      default:
        return "ring-2 ring-blue-500 bg-blue-50";
    }
  };

  return (
    <motion.tr
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, y: -10 }}
      animate={{ 
        opacity: isDragging || isSortableDragging ? 0.5 : 1,
        y: 0,
        scale: isDragging || isSortableDragging ? 0.95 : 1,
      }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={`${level > 0 ? "bg-gray-50/50" : ""} ${
        getDragOverClasses()
      } ${isSelected ? "bg-blue-100 ring-2 ring-blue-300" : ""} ${
        isDragging || isSortableDragging ? "z-50" : ""
      }`}
    >
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <div className="flex items-center" style={{ marginLeft: `${level * 24}px` }}>
            {level > 0 && (
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <div
                    className={`w-3 h-3 border-l-2 border-b-2 border-gray-300 ${
                      isLast ? "" : "border-b-0"
                    }`}
                  ></div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              {/* Чекбокс для группового выбора */}
              {isMultiSelectMode && onSelect && (
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={(checked) => onSelect(item.kls_id, checked === true)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              
              {/* Иконка для перетаскивания */}
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
                title="Перетащите для изменения позиции"
              >
                <GripVertical className="h-4 w-4" />
              </div>

              {level === 0 ? (
                <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center">
                  <Layers className="h-4 w-4 text-blue-600" />
                </div>
              ) : (
                <div className="w-5 h-5 bg-green-100 rounded flex items-center justify-center">
                  <FileText className="h-3 w-3 text-green-600" />
                </div>
              )}

              <div className="flex flex-col">
                <span
                  className={level === 0 ? "font-semibold text-gray-900" : "text-gray-700"}
                >
                  {item.kls_namef}
                </span>
                {hasChildren && (
                  <span className="text-xs text-gray-500">
                    {childrenCount} раздел{childrenCount === 1 ? "" : childrenCount < 5 ? "а" : "ов"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <Badge variant="outline" className="text-xs w-fit">
            {item.kls_code}
          </Badge>
          <span className="text-xs text-muted-foreground font-mono" title={item.kls_rubrika}>
            {item.kls_rubrika}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <PermissionGuard permission="kls.kls.edit">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fullItem && onEdit(fullItem)}
              disabled={!fullItem || fullItem.kls_is_del}
              className="h-8 w-8"
              title={fullItem ? "Редактировать" : "Полные данные не загружены"}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="kls.kls.delete">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fullItem && onDelete(fullItem)}
              disabled={!fullItem || fullItem.kls_is_del}
              className="h-8 w-8"
              title={fullItem ? "Удалить" : "Полные данные не загружены"}
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </PermissionGuard>
        </div>
      </TableCell>
    </motion.tr>
  );
}

export interface KlsManagementProps {
  /** Предзаполнить фильтр по квалификатору (например 345 — семантические типы геометрий). */
  initialQualId?: number;
  /** Скрыть заголовок «Разделы классификаторов», если заголовок уже задан снаружи (вкладка «Семантика геометрий»). */
  hideTitle?: boolean;
}

export default function KlsManagement({ initialQualId, hideTitle = false }: KlsManagementProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Kls | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQualId, setSelectedQualId] = useState<number | undefined>(undefined);
  const [showDeleted, setShowDeleted] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "hierarchy">("table");
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    item: Kls | null;
  }>({ isOpen: false, item: null });
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [sortField, setSortField] = useState<"created_at" | "updated_at">("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [hierarchyFilter, setHierarchyFilter] = useState("");
  const [activeId, setActiveId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<Set<number>>(new Set());
  const [moveHistory, setMoveHistory] = useState<Array<{
    klsId: number;
    oldRubrika: string;
    newRubrika: string;
    timestamp: Date;
  }>>([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const dragOverPosition = useRef<"before" | "after" | "inside">("inside");

  useEffect(() => {
    if (initialQualId != null) {
      setSelectedQualId(initialQualId);
    }
  }, [initialQualId]);

  // Настройка сенсоров для drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Минимальное расстояние для начала перетаскивания
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Форма с валидацией
  const form = useForm<KlsFormData>({
    resolver: zodResolver(klsSchema),
    defaultValues: {
      qual_id: 0,
      kls_namef: "",
      kls_names: "",
      kls_note: "",
      kls_code: "",
      kls_vers: 1,
      kls_rubrika: "",
    },
  });

  // Загрузка квалификаторов для селектора
  const { data: quals = [] } = useQuery({
    queryKey: ["quals"],
    queryFn: () => qualsApi.list({ include_deleted: false }),
  });

  // Загрузка списка разделов
  const { data: klsList = [], isLoading } = useQuery({
    queryKey: ["kls", selectedQualId, showDeleted],
    queryFn: () =>
      klsSectionsApi.list({
        qual_id: selectedQualId,
        include_deleted: showDeleted,
      }),
  });

  // Загрузка иерархии разделов (только для иерархического режима)
  const { data: hierarchy = [], isLoading: isLoadingHierarchy } = useQuery({
    queryKey: ["kls", "hierarchy", selectedQualId, showDeleted],
    queryFn: () => klsSectionsApi.getHierarchy(selectedQualId!),
    enabled: !!selectedQualId && viewMode === "hierarchy",
  });

  // Мутация создания
  const createMutation = useMutation({
    mutationFn: (data: KlsCreate) => klsSectionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kls"] });
      toast({
        title: "Успешно",
        description: "Раздел классификатора создан",
      });
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось создать раздел классификатора",
        variant: "destructive",
      });
    },
  });

  // Мутация обновления
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: KlsUpdate }) =>
      klsSectionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kls"] });
      toast({
        title: "Успешно",
        description: "Раздел классификатора обновлен",
      });
      setIsDialogOpen(false);
      setEditingItem(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось обновить раздел классификатора",
        variant: "destructive",
      });
    },
  });

  // Мутация удаления
  const deleteMutation = useMutation({
    mutationFn: (id: number) => klsSectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kls"] });
      toast({
        title: "Успешно",
        description: "Раздел классификатора удален",
      });
      setDeleteConfirm({ isOpen: false, item: null });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.response?.data?.detail || "Не удалось удалить раздел классификатора",
        variant: "destructive",
      });
    },
  });

  // Мутация перемещения узла
  const moveMutation = useMutation({
    mutationFn: ({ klsId, moveRequest }: { klsId: number; moveRequest: KlsMoveRequest }) =>
      klsSectionsApi.move(klsId, moveRequest),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["kls", "hierarchy"] });
      queryClient.invalidateQueries({ queryKey: ["kls"] });
      toast({
        title: "Успешно",
        description: data.message,
      });
      setActiveId(null);
      setDragOverId(null);
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка перемещения",
        description: error?.response?.data?.detail || "Не удалось переместить узел",
        variant: "destructive",
      });
      setActiveId(null);
      setDragOverId(null);
    },
  });

  // Открытие диалога создания/редактирования
  const openDialog = (item?: Kls) => {
    if (item) {
      setEditingItem(item);
      form.reset({
        qual_id: item.qual_id,
        kls_namef: item.kls_namef,
        kls_names: item.kls_names || "",
        kls_note: item.kls_note || "",
        kls_code: item.kls_code,
        kls_vers: item.kls_vers,
        kls_rubrika: item.kls_rubrika,
      });
    } else {
      setEditingItem(null);
      form.reset({
        qual_id: selectedQualId || 0,
        kls_namef: "",
        kls_names: "",
        kls_note: "",
        kls_code: "",
        kls_vers: 1,
        kls_rubrika: "",
      });
    }
    setIsDialogOpen(true);
  };

  // Обработка отправки формы
  const onSubmit = (data: KlsFormData) => {
    if (editingItem) {
      updateMutation.mutate({
        id: editingItem.kls_id,
        data: {
          qual_id: data.qual_id,
          kls_namef: data.kls_namef,
          kls_names: data.kls_names || undefined,
          kls_note: data.kls_note || undefined,
          kls_code: data.kls_code,
          kls_vers: data.kls_vers,
          kls_rubrika: data.kls_rubrika,
        },
      });
    } else {
      createMutation.mutate({
        qual_id: data.qual_id,
        kls_namef: data.kls_namef,
        kls_names: data.kls_names || undefined,
        kls_note: data.kls_note || undefined,
        kls_code: data.kls_code,
        kls_vers: data.kls_vers,
        kls_rubrika: data.kls_rubrika,
      });
    }
  };

  // Фильтрация и сортировка по датам
  const filteredKls = useMemo(() => {
    const base = klsList.filter((item) => {
      if (item.kls_is_del && !showDeleted) return false;
      if (selectedQualId && item.qual_id !== selectedQualId) return false;
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.kls_namef.toLowerCase().includes(query) ||
        item.kls_names?.toLowerCase().includes(query) ||
        item.kls_code.toLowerCase().includes(query) ||
        item.kls_rubrika.toLowerCase().includes(query)
      );
    });

    return base.slice().sort((a, b) => {
      const aDate = sortField === "created_at" ? a.created_at : a.updated_at || a.created_at;
      const bDate = sortField === "created_at" ? b.created_at : b.updated_at || b.created_at;

      const aTime = aDate ? new Date(aDate).getTime() : 0;
      const bTime = bDate ? new Date(bDate).getTime() : 0;

      if (sortDirection === "asc") {
        return aTime - bTime;
      }
      return bTime - aTime;
    });
  }, [klsList, showDeleted, selectedQualId, searchQuery, sortField, sortDirection]);

  const handleSortClick = (field: "created_at" | "updated_at") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Используем иерархию с бэкенда (уже построенную)
  const baseHierarchy = hierarchy;

  // Функция для подсчета общего количества детей (включая вложенные)
  const countAllChildren = (item: any): number => {
    if (!item.children || item.children.length === 0) return 0;
    return item.children.length + item.children.reduce((sum: number, child: any) => sum + countAllChildren(child), 0);
  };

  // Получаем все ID элементов для SortableContext
  const getAllIds = (items: KlsHierarchyItem[]): number[] => {
    const ids: number[] = [];
    for (const item of items) {
      ids.push(item.kls_id);
      if (item.children && item.children.length > 0) {
        ids.push(...getAllIds(item.children));
      }
    }
    return ids;
  };

  // Рекурсивная функция для отображения иерархии в табличном виде с drag-and-drop
  const renderHierarchy = (
    items: KlsHierarchyItem[],
    level = 0,
    _parentIndex = 0,
    _totalSiblings = 0
  ) => {
    return items.flatMap((item, index) => {
      const isLast = index === items.length - 1;
      const hasChildren = item.children && item.children.length > 0;
      const childrenCount = hasChildren ? countAllChildren(item) : 0;

      // Находим полный объект Kls из klsList для редактирования/удаления
      const fullItem = klsList.find((k) => k.kls_id === item.kls_id) || null;

      const rows = [
        <DraggableHierarchyItem
          key={item.kls_id}
          item={item}
          level={level}
          isLast={isLast}
          hasChildren={hasChildren}
          childrenCount={childrenCount}
          fullItem={fullItem}
          onEdit={openDialog}
          onDelete={(item) => setDeleteConfirm({ isOpen: true, item })}
          isDragging={activeId === item.kls_id}
          isDragOver={dragOverId === item.kls_id}
          isSelected={selectedNodes.has(item.kls_id)}
          onSelect={handleNodeSelect}
          isMultiSelectMode={isMultiSelectMode}
          dragOverPosition={dragOverId === item.kls_id ? dragOverPosition.current : "inside"}
        />,
      ];

      // Рекурсивно добавляем дочерние элементы
      if (hasChildren) {
        rows.push(...renderHierarchy(item.children, level + 1, index, items.length));
      }

      return rows;
    });
  };

  // Вспомогательная функция для поиска узла в иерархии по ID
  const findNodeInHierarchy = (
    items: KlsHierarchyItem[],
    id: number
  ): KlsHierarchyItem | null => {
    for (const item of items) {
      if (item.kls_id === id) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findNodeInHierarchy(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // Обработчик начала перетаскивания
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as number);
  };

  // Обработчик перетаскивания над элементом с определением позиции
  const handleDragOver = (event: DragOverEvent) => {
    const { over, active } = event;
    if (!over || active.id === over.id) {
      setDragOverId(null);
      dragOverPosition.current = "inside";
      return;
    }

    setDragOverId(over.id as number);
    
    // Определяем позицию относительно целевого элемента
    const targetNode = findNodeInHierarchy(baseHierarchy, over.id as number);
    if (!targetNode) {
      dragOverPosition.current = "inside";
      return;
    }

    // Получаем братьев целевого узла
    const targetRubrikaParts = targetNode.kls_rubrika.split(".");
    const parentRubrika = targetRubrikaParts.length > 1 
      ? targetRubrikaParts.slice(0, -1).join(".")
      : null;
    
    // Находим всех братьев
    const getSiblings = (items: KlsHierarchyItem[], parentPath: string | null): KlsHierarchyItem[] => {
      if (parentPath === null) {
        return items.filter(item => !item.kls_rubrika.includes("."));
      }
      return items.filter(item => 
        item.kls_rubrika.startsWith(parentPath + ".") &&
        item.kls_rubrika.split(".").length === parentPath.split(".").length + 1
      );
    };

    const siblings = getSiblings(baseHierarchy, parentRubrika);
    const targetIndex = siblings.findIndex(s => s.kls_id === over.id as number);
    const draggedIndex = siblings.findIndex(s => s.kls_id === active.id as number);

    // Определяем позицию: before, after или inside
    if (targetNode.children && targetNode.children.length > 0) {
      // Если у целевого узла есть дети, можно переместить внутрь
      dragOverPosition.current = "inside";
    } else if (draggedIndex !== -1 && draggedIndex < targetIndex) {
      // Перетаскиваемый элемент находится перед целевым
      dragOverPosition.current = "after";
    } else {
      // Перетаскиваемый элемент находится после целевого или это другой уровень
      dragOverPosition.current = "before";
    }
  };

  // Вспомогательная функция для получения братьев узла
  const getSiblings = useCallback((items: KlsHierarchyItem[], parentPath: string | null): KlsHierarchyItem[] => {
    const result: KlsHierarchyItem[] = [];
    
    const traverse = (nodes: KlsHierarchyItem[]) => {
      for (const node of nodes) {
        const nodeParentPath = node.kls_rubrika.split(".").slice(0, -1).join(".") || null;
        if (nodeParentPath === parentPath) {
          result.push(node);
        }
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
    
    traverse(items);
    return result;
  }, []);

  // Обработчик завершения перетаскивания с точным позиционированием
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      setDragOverId(null);
      dragOverPosition.current = "inside";
      return;
    }

    const draggedNode = findNodeInHierarchy(baseHierarchy, active.id as number);
    const targetNode = findNodeInHierarchy(baseHierarchy, over.id as number);

    if (!draggedNode || !targetNode) {
      setActiveId(null);
      setDragOverId(null);
      dragOverPosition.current = "inside";
      return;
    }

    // Проверка: нельзя переместить узел в самого себя или в своего потомка
    const draggedRubrika = draggedNode.kls_rubrika;
    const targetRubrika = targetNode.kls_rubrika;
    
    // Если целевой узел является потомком перемещаемого узла, отменяем операцию
    if (targetRubrika.startsWith(draggedRubrika + ".")) {
      toast({
        title: "Ошибка",
        description: "Нельзя переместить узел в его собственного потомка",
        variant: "destructive",
      });
      setActiveId(null);
      setDragOverId(null);
      dragOverPosition.current = "inside";
      return;
    }

    // Определяем новую позицию с учетом точного позиционирования
    let targetParentRubrika: string | null = null;
    let position: number | null = null;

    if (dragOverPosition.current === "inside" && targetNode.children && targetNode.children.length > 0) {
      // Перемещаем внутрь целевого узла
      targetParentRubrika = targetNode.kls_rubrika;
      position = null; // В конец
    } else {
      // Перемещаем на тот же уровень, что и целевой узел
      const targetRubrikaParts = targetNode.kls_rubrika.split(".");
      if (targetRubrikaParts.length > 1) {
        targetRubrikaParts.pop();
        targetParentRubrika = targetRubrikaParts.join(".");
      } else {
        // Корневой уровень
        targetParentRubrika = null;
      }

      // Определяем точную позицию среди братьев
      const siblings = getSiblings(baseHierarchy, targetParentRubrika);
      const targetIndex = siblings.findIndex(s => s.kls_id === over.id as number);
      
      if (dragOverPosition.current === "before") {
        // Вставляем перед целевым узлом
        position = targetIndex;
      } else if (dragOverPosition.current === "after") {
        // Вставляем после целевого узла
        position = targetIndex + 1;
      } else {
        position = null; // В конец
      }
    }

    // Сохраняем в историю перед перемещением
    const oldRubrika = draggedNode.kls_rubrika;
    
    // Выполняем перемещение
    moveMutation.mutate({
      klsId: active.id as number,
      moveRequest: {
        target_parent_rubrika: targetParentRubrika,
        position: position,
      },
    }, {
      onSuccess: (data) => {
        // Добавляем в историю после успешного перемещения
        setMoveHistory(prev => [{
          klsId: active.id as number,
          oldRubrika: oldRubrika,
          newRubrika: data.new_rubrika,
          timestamp: new Date(),
        }, ...prev].slice(0, 50)); // Храним последние 50 операций
      },
    });
  };

  // Обработчик отмены перетаскивания
  const handleDragCancel = () => {
    setActiveId(null);
    setDragOverId(null);
    dragOverPosition.current = "inside";
  };

  // Функции для группового выбора
  const handleNodeSelect = useCallback((id: number, selected: boolean) => {
    setSelectedNodes(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = getAllIds(baseHierarchy);
    setSelectedNodes(new Set(allIds));
  }, [baseHierarchy]);

  const handleDeselectAll = useCallback(() => {
    setSelectedNodes(new Set());
  }, []);

  // Функция отката последнего перемещения
  const handleUndoLastMove = useCallback(() => {
    if (moveHistory.length === 0) {
      toast({
        title: "История пуста",
        description: "Нет операций для отката",
        variant: "destructive",
      });
      return;
    }

    const lastMove = moveHistory[0];
    const node = findNodeInHierarchy(baseHierarchy, lastMove.klsId);
    if (!node) {
      toast({
        title: "Ошибка",
        description: "Узел не найден",
        variant: "destructive",
      });
      return;
    }

    // Определяем родителя из старой рубрики
    const oldRubrikaParts = lastMove.oldRubrika.split(".");
    const oldParentRubrika = oldRubrikaParts.length > 1
      ? oldRubrikaParts.slice(0, -1).join(".")
      : null;

    moveMutation.mutate({
      klsId: lastMove.klsId,
      moveRequest: {
        target_parent_rubrika: oldParentRubrika,
        position: null,
      },
    }, {
      onSuccess: () => {
        // Удаляем операцию из истории
        setMoveHistory(prev => prev.slice(1));
        toast({
          title: "Откат выполнен",
          description: "Последнее перемещение отменено",
        });
      },
    });
  }, [moveHistory, baseHierarchy, moveMutation, toast]);

  // Фильтрация иерархии по коду / рубрике / наименованию
  const filteredHierarchy = useMemo(() => {
    if (!hierarchyFilter) {
      return baseHierarchy;
    }

    const query = hierarchyFilter.toLowerCase();

    const filterRecursive = (items: any[]): any[] => {
      const result: any[] = [];

      for (const item of items) {
        const selfMatch =
          item.kls_code.toLowerCase().includes(query) ||
          item.kls_rubrika.toLowerCase().includes(query) ||
          item.kls_namef.toLowerCase().includes(query);

        const children = item.children ? filterRecursive(item.children) : [];

        if (selfMatch || children.length > 0) {
          result.push({
            ...item,
            children,
          });
        }
      }

      return result;
    };

    return filterRecursive(baseHierarchy);
  }, [baseHierarchy, hierarchyFilter]);

  const klsToolbarActions = (
    <>
      <PermissionGuard permission="kls.export">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExportDialogOpen(true)}
          disabled={!selectedQualId}
        >
          <Download className="mr-2 h-4 w-4" />
          Экспорт
        </Button>
      </PermissionGuard>
      <PermissionGuard permission="kls.import">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsImportDialogOpen(true)}
          disabled={!selectedQualId}
        >
          <Upload className="mr-2 h-4 w-4" />
          Импорт
        </Button>
      </PermissionGuard>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowDeleted(!showDeleted)}
      >
        {showDeleted ? "Скрыть удаленные" : "Показать удаленные"}
      </Button>
      <PermissionGuard permission="kls.kls.create">
        <Button onClick={() => openDialog()} disabled={!selectedQualId}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить раздел
        </Button>
      </PermissionGuard>
    </>
  );

  return (
    <div className="space-y-4">
      {hideTitle ? (
        <div className="flex justify-end items-start">
          <div className="flex gap-2 flex-wrap justify-end shrink-0">{klsToolbarActions}</div>
        </div>
      ) : (
        <ReferencesSectionHeader
          title="Разделы классификаторов"
          description="Выберите квалификатор — отобразятся разделы KLS (значения): рубрика ltree, коды, наименования. Доступны режимы «Таблица» и «Иерархия»."
          actions={klsToolbarActions}
        />
      )}

      {/* Фильтры */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по наименованию, коду или рубрике..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={() => setSearchQuery("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Combobox
          options={[
            { value: "all", label: "Все квалификаторы" },
            ...quals.map((qual) => ({
              value: qual.qual_id.toString(),
              label: qual.qual_namef,
            })),
          ]}
          value={selectedQualId?.toString() || "all"}
          onValueChange={(value) => {
            const qualId = value === "all" || !value ? undefined : parseInt(value);
            setSelectedQualId(qualId);
            if (!qualId) {
              setViewMode("table");
            }
          }}
          placeholder="Выберите квалификатор"
          searchPlaceholder="Поиск квалификатора..."
          emptyText="Квалификаторы не найдены"
          className="w-[250px]"
          allowClear={true}
          clearText="Все квалификаторы"
        />
      </div>

      {/* Переключение вида */}
      {selectedQualId && (
        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as "table" | "hierarchy")}>
          <TabsList>
            <TabsTrigger value="table">
              <List className="mr-2 h-4 w-4" />
              Таблица
            </TabsTrigger>
            <TabsTrigger value="hierarchy">
              <TreePine className="mr-2 h-4 w-4" />
              Иерархия
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* Табличный вид */}
      {viewMode === "table" && (
        <div className="border rounded-md overflow-hidden">
          <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Квалификатор</TableHead>
                  <TableHead>Полное наименование</TableHead>
                  <TableHead>Сокращенное наименование</TableHead>
                  <TableHead>Код</TableHead>
                  <TableHead>Рубрика</TableHead>
                  <TableHead>Версия</TableHead>
                  <TableHead>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs font-medium hover:text-foreground"
                      onClick={() => handleSortClick("created_at")}
                    >
                      Создан
                      {sortField === "created_at" && (
                        <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs font-medium hover:text-foreground"
                      onClick={() => handleSortClick("updated_at")}
                    >
                      Обновлён
                      {sortField === "updated_at" && (
                        <span>{sortDirection === "asc" ? "↑" : "↓"}</span>
                      )}
                    </button>
                  </TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead className="text-right">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-sm text-muted-foreground py-6">
                      Загрузка...
                    </TableCell>
                  </TableRow>
                ) : filteredKls.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-sm text-muted-foreground py-6">
                      {searchQuery || selectedQualId
                        ? "Ничего не найдено"
                        : "Выберите квалификатор для просмотра разделов"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredKls.map((item) => (
                    <TableRow key={item.kls_id}>
                      <TableCell>{item.kls_id}</TableCell>
                      <TableCell>
                        {quals.find((q) => q.qual_id === item.qual_id)?.qual_namef || "-"}
                      </TableCell>
                      <TableCell className="font-medium">{item.kls_namef}</TableCell>
                      <TableCell>{item.kls_names || "-"}</TableCell>
                      <TableCell>{item.kls_code}</TableCell>
                      <TableCell className="font-mono text-xs">{item.kls_rubrika}</TableCell>
                      <TableCell>{item.kls_vers}</TableCell>
                      <TableCell
                        className="text-xs text-muted-foreground"
                        title={new Date(item.created_at).toLocaleString("ru-RU")}
                      >
                        {new Date(item.created_at).toLocaleDateString("ru-RU")}
                      </TableCell>
                      <TableCell
                        className="text-xs text-muted-foreground"
                        title={
                          item.updated_at
                            ? new Date(item.updated_at).toLocaleString("ru-RU")
                            : "Нет обновлений"
                        }
                      >
                        {item.updated_at
                          ? new Date(item.updated_at).toLocaleDateString("ru-RU")
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {item.kls_is_del ? (
                          <Badge variant="destructive">Удален</Badge>
                        ) : (
                          <Badge variant="default">Активен</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <PermissionGuard permission="kls.kls.edit">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog(item)}
                            disabled={item.kls_is_del}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="kls.kls.delete">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm({ isOpen: true, item })}
                            disabled={item.kls_is_del}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        </div>
      )}

      {/* Иерархический вид */}
      {viewMode === "hierarchy" && selectedQualId && (
        <div className="border rounded-md overflow-hidden">
          <div className="p-3 border-b bg-muted/40 flex gap-3 items-center flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Быстрый фильтр по коду, рубрике или наименованию..."
                value={hierarchyFilter}
                onChange={(e) => setHierarchyFilter(e.target.value)}
                className="pl-9 h-8 text-xs"
              />
            </div>
            {hierarchyFilter && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs"
                onClick={() => setHierarchyFilter("")}
              >
                <X className="h-4 w-4 mr-1" />
                Сброс
              </Button>
            )}
            
            {/* Кнопки управления групповым выбором */}
            <div className="flex gap-2 items-center">
              <Button
                variant={isMultiSelectMode ? "default" : "outline"}
                size="sm"
                className="h-8 px-2 text-xs"
                onClick={() => {
                  setIsMultiSelectMode(!isMultiSelectMode);
                  if (isMultiSelectMode) {
                    setSelectedNodes(new Set());
                  }
                }}
              >
                {isMultiSelectMode ? <CheckSquare className="h-4 w-4 mr-1" /> : <Square className="h-4 w-4 mr-1" />}
                {isMultiSelectMode ? "Отменить выбор" : "Групповой выбор"}
              </Button>
              
              {isMultiSelectMode && selectedNodes.size > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs"
                    onClick={handleSelectAll}
                  >
                    Выбрать все
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 text-xs"
                    onClick={handleDeselectAll}
                  >
                    Снять выбор ({selectedNodes.size})
                  </Button>
                </>
              )}
            </div>

            {/* Кнопка отката */}
            {moveHistory.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs"
                onClick={handleUndoLastMove}
                title={`Откатить последнее перемещение (${moveHistory.length} операций в истории)`}
              >
                <Undo2 className="h-4 w-4 mr-1" />
                Откат ({moveHistory.length})
              </Button>
            )}
          </div>
          <div className="mb-2 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
            💡 <strong>Подсказка:</strong> Перетащите узел за иконку <GripVertical className="h-3 w-3 inline" /> для изменения его позиции в иерархии
          </div>
          <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
            {isLoadingHierarchy ? (
              <div className="text-center text-sm text-muted-foreground py-6">Загрузка...</div>
            ) : baseHierarchy.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-6">
                Иерархия разделов отсутствует
              </div>
            ) : filteredHierarchy.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-6">
                По данному фильтру ничего не найдено
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <SortableContext
                  items={getAllIds(filteredHierarchy)}
                  strategy={verticalListSortingStrategy}
                >
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10">
                      <TableRow>
                        <TableHead>Название и иерархия</TableHead>
                        <TableHead>Код и рубрика</TableHead>
                        <TableHead className="text-right">Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{renderHierarchy(filteredHierarchy)}</TableBody>
                  </Table>
                </SortableContext>
                <DragOverlay>
                  <AnimatePresence>
                    {activeId && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white p-3 rounded-lg shadow-xl border-2 border-blue-500"
                      >
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium">
                            {findNodeInHierarchy(filteredHierarchy, activeId)?.kls_namef || ""}
                          </span>
                          {selectedNodes.has(activeId) && selectedNodes.size > 1 && (
                            <Badge variant="secondary" className="text-xs">
                              +{selectedNodes.size - 1}
                            </Badge>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </DragOverlay>
              </DndContext>
            )}
          </div>
        </div>
      )}

      {/* Диалог создания/редактирования */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? "Редактировать раздел классификатора" : "Создать раздел классификатора"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="qual_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Квалификатор *</FormLabel>
                    <Select
                      value={field.value?.toString()}
                      onValueChange={(value) => field.onChange(parseInt(value))}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Выберите квалификатор" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {quals.map((qual) => (
                          <SelectItem key={qual.qual_id} value={qual.qual_id.toString()}>
                            {qual.qual_namef}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="kls_namef"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Полное наименование *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Москва" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="kls_names"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Сокращенное наименование</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="МСК" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="kls_note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Описание</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Описание раздела" rows={3} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="kls_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Код раздела *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="45000000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="kls_vers"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Версия</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                          min={1}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="kls_rubrika"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Рубрика (ltree путь) *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="1.2.3" className="font-mono" />
                    </FormControl>
                    <FormDescription>
                      Иерархический путь в формате ltree (например: 1.2.3 или a.b.c)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    form.reset();
                    setEditingItem(null);
                  }}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingItem ? "Сохранить" : "Создать"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, item: null })}
        onConfirm={() => {
          if (deleteConfirm.item) {
            deleteMutation.mutate(deleteConfirm.item.kls_id);
          }
        }}
        title="Удалить раздел классификатора?"
        description={`Вы уверены, что хотите удалить раздел "${deleteConfirm.item?.kls_namef}"?`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="destructive"
      />
      
      {/* Диалоги импорта/экспорта */}
      <ImportDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        entityType="kls"
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["kls"] });
          queryClient.invalidateQueries({ queryKey: ["kls", "hierarchy"] });
        }}
      />
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        entityType="kls"
        qualId={selectedQualId}
      />
    </div>
  );
}
